import { store } from "./state/store.js";
import { wireFileInput } from "./data/wireFileInput.js";
import { loadManifest, loadOnlineJson } from "./data/loadOnlineJson.js";
import { loadJsonData } from "./data/loadJson.js";
import { wireTabs } from "./ui/tabs.js";
import { wireEvents } from "./ui/events.js";
import { setupProgramTooltip } from "./ui/tooltip.js";
import { updateUI } from "./ui/updateUI.js";
import { setupMobileLayout, setupMobileOrientationGuard } from "./ui/mobile.js";
import { setupDesktopViewportSync } from "./ui/viewport.js";
import { exportSelectionWithLogo } from "./export/exportExcel.js";
import { el } from "./utils/dom.js";

function isOnlineMode() {
  return (
    window.location.protocol === "http:" ||
    window.location.protocol === "https:"
  );
}

function rebuildSatFilter() {
  const satFilter = document.getElementById("satFilter");
  if (!satFilter) return;

  const currentValue = satFilter.value || "";

  const sats = [...new Set(
    (store.transponders || [])
      .map(tp => String(tp?.sat_pos ?? "").trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, "de"));

  satFilter.innerHTML = '<option value="">Alle</option>';

  for (const sat of sats) {
    const opt = document.createElement("option");
    opt.value = sat;
    opt.textContent = sat;
    satFilter.appendChild(opt);
  }

  if (currentValue && sats.includes(currentValue)) {
    satFilter.value = currentValue;
  }
}

function updateSelectionHeaderCount() {
  const selCountHeader = document.getElementById("selCountHeader");
  if (selCountHeader) {
    selCountHeader.textContent = String(store.selection?.size || 0);
  }
}

function applyModeUI() {
  const modeBanner = document.getElementById("modeBanner");
  const onlineLoader = document.getElementById("onlineLoader");
  const preLoadRow = document.getElementById("preLoadRow");

  const modeBadge = document.getElementById("modeBadge");
  const modeText = document.getElementById("modeText");
  const modeHint = document.getElementById("modeHint");

  const changeBtn = document.getElementById("changeFileBtn");
  const fileName = document.getElementById("loadedFileName");
  const loaderHint = document.querySelector(".loader-hint");
  const badgeType = document.getElementById("badgeType");
  const onlineModeBadge = document.getElementById("onlineModeBadge");
  const onlineStatusText = document.getElementById("onlineStatusText");
  const onlineHint = "Online-Datenbanken stehen nur im Server-Modus bereit.";

  if (isOnlineMode()) {
    if (modeBanner) modeBanner.style.display = "none";
    if (onlineLoader) onlineLoader.style.display = "";
    if (preLoadRow) preLoadRow.style.display = "none";

    if (onlineModeBadge) {
      onlineModeBadge.textContent = "Online-Modus";
      onlineModeBadge.title = onlineHint;
    }
    if (onlineStatusText) {
      onlineStatusText.textContent = "Server-Datenbank aktiv";
      onlineStatusText.title = onlineHint;
    }

    if (modeBadge) modeBadge.textContent = "Online";
    if (modeText) modeText.textContent = "Server-Datenbank aktiv";
    if (modeHint) modeHint.textContent = "";

    if (changeBtn) changeBtn.style.display = "none";
    if (fileName) fileName.style.display = "none";
    if (loaderHint) loaderHint.style.display = "none";

    // Dateityp im Online-Modus ausblenden
    if (badgeType) badgeType.style.display = "none";
  } else {
    if (modeBanner) modeBanner.style.display = "";
    if (onlineLoader) onlineLoader.style.display = "none";
    if (preLoadRow) preLoadRow.style.display = "";

    if (modeBadge) modeBadge.textContent = "Offline-Modus";
    if (modeText) modeText.textContent = "Lokale Datei laden";
    if (modeHint) modeHint.textContent = "Online-Datenbanken sind nur ueber http/https verfuegbar.";

    if (onlineModeBadge) {
      onlineModeBadge.textContent = "";
      onlineModeBadge.title = "";
    }
    if (onlineStatusText) {
      onlineStatusText.textContent = "";
      onlineStatusText.title = "";
    }

    if (changeBtn) changeBtn.style.display = "";
    if (fileName) fileName.style.display = "";
    if (loaderHint) loaderHint.style.display = "";
    if (badgeType) badgeType.style.display = "";
  }
}

function afterDatabaseLoad(displayName = "") {
  const loadedBar = document.getElementById("loadedBar");
  const loadedFileName = document.getElementById("loadedFileName");
  const status = document.getElementById("status");
  const loadStats = document.getElementById("loadStats");

  document.body.classList.add("excel-loaded");

  if (loadedBar) {
    loadedBar.hidden = false;
  }

  if (loadedFileName && !isOnlineMode()) {
    loadedFileName.textContent = displayName || "Datenbank geladen";
    loadedFileName.title = displayName || "Datenbank geladen";
  }

  if (status) {
    status.textContent = "Datenbank erfolgreich geladen.";
  }

  if (loadStats) {
    const tpCount = Array.isArray(store.transponders) ? store.transponders.length : 0;
    const prgCount = Array.isArray(store.programs) ? store.programs.length : 0;
    loadStats.textContent = `Transponder: ${tpCount} | Programme: ${prgCount}`;
  }

  rebuildSatFilter();
  updateSelectionHeaderCount();
  updateUI();
}

async function loadOnlineSelection(path, hintEl = null) {
  const json = await loadOnlineJson(path);
  const result = loadJsonData(json, path);

  afterDatabaseLoad(path);

  if (hintEl) {
    hintEl.textContent = "";
  }

  console.log("Online JSON geladen:", result);
}

async function setupOnlineLoader() {
  const select = document.getElementById("jsonSelect");
  const btn = document.getElementById("loadJsonBtn");
  const hint = document.getElementById("dataModeHint");

  if (!select || !btn) return;
  if (!isOnlineMode()) return;

  try {
    await loadManifest(select);

    const files = Array.from(select.options).filter(
      (opt) => opt.value && opt.value.trim() !== ""
    );

    if (files.length === 1) {
      select.style.display = "none";
      btn.style.display = "none";

      await loadOnlineSelection(files[0].value, hint);
    } else if (files.length > 1) {
      const firstRealOption = files[0];
      select.value = firstRealOption.value;
      await loadOnlineSelection(firstRealOption.value, hint);
    } else {
      select.disabled = true;
      btn.disabled = true;
      if (hint) hint.textContent = "Keine Online-Datenbank gefunden.";
      return;
    }
  } catch (err) {
    console.error("Manifest konnte nicht geladen werden:", err);

    select.disabled = true;
    btn.disabled = true;

    if (hint) {
      hint.textContent = err?.message || "Keine Online-Liste verfuegbar.";
    }
    return;
  }

  btn.addEventListener("click", async () => {
    const path = select.value;
    if (!path) return;

    btn.disabled = true;

    try {
      await loadOnlineSelection(path, hint);
    } catch (err) {
      console.error("Online JSON konnte nicht geladen werden:", err);
      if (hint) {
        hint.textContent = err?.message || "Online-JSON konnte nicht geladen werden.";
      }
      alert(err?.message || "Online-JSON konnte nicht geladen werden.");
    } finally {
      btn.disabled = false;
    }
  });
}

function init() {
  window.store = store;

  applyModeUI();

  wireTabs();
  wireEvents();
  wireFileInput();
  setupProgramTooltip();
  setupMobileLayout(() => updateUI());
  setupMobileOrientationGuard();
  setupDesktopViewportSync();

  el("exportBtn")?.addEventListener("click", exportSelectionWithLogo);

  setupOnlineLoader();

  updateSelectionHeaderCount();
  updateUI();
}

if (document.readyState === "loading") {
  window.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
